/**
 * Download a conversation as a file.
 *
 * A single conversation is fetched by navigating a hidden anchor at the export
 * URL, the same way a document download works: the cookie travels with the
 * request and the browser handles the save dialog on its own.
 *
 * Several conversations need a POST (the ids go in the body, not the URL), and
 * a POST cannot be triggered by an anchor. That one goes through fetch and
 * turns the response into an object URL - the only place in the app that does
 * so, which is why the revoke is spelled out rather than left to the reader.
 */

import { getApiFetchUrl } from '../../config/apiConfig';
import { CSRF_HEADER_NAME, getCsrfToken } from '../auth/csrfManager';
import { log } from '../errors/logger';

/** Formats the backend renders. */
export type ExportFormat = 'md' | 'txt';

/**
 * Read the file name the server chose.
 *
 * The header carries the name twice: `filename*=UTF-8''...` percent-encoded,
 * which is what survives umlauts, and a plain `filename="..."` for clients that
 * do not know the first. Prefer the encoded one, fall back to the plain one,
 * and if the header is missing entirely (a proxy may strip it) use the name
 * passed in, so the download never lands as "download".
 */
function filenameFromHeader(header: string | null, fallback: string): string {
  if (!header) {
    return fallback;
  }
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1]);
    } catch {
      // A malformed header must not cost the user their download.
    }
  }
  const plain = /filename="([^"]+)"/i.exec(header);
  return plain?.[1] ?? fallback;
}

/** Click a hidden anchor, then clean it up. */
function saveViaAnchor(href: string, filename?: string): void {
  const anchor = document.createElement('a');
  anchor.href = href;
  if (filename) {
    anchor.download = filename;
  }
  anchor.rel = 'noopener noreferrer';
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}

/** Download one conversation. */
export function exportConversation(conversationId: string, format: ExportFormat): void {
  if (!conversationId) {
    log.error('No conversation ID provided for export');
    return;
  }
  const url = `${getApiFetchUrl()}/conversations/${encodeURIComponent(conversationId)}/export?format=${format}`;
  saveViaAnchor(url);
  log.info('Conversation export requested', { conversationId, format });
}

/**
 * Download several conversations as one ZIP.
 *
 * Throws on failure so the caller can show a message; a silent no-op would
 * leave the user waiting for a file that never arrives.
 */
export async function exportConversations(
  conversationIds: string[],
  format: ExportFormat
): Promise<void> {
  if (conversationIds.length === 0) {
    return;
  }

  const csrf = getCsrfToken();
  const response = await fetch(`${getApiFetchUrl()}/conversations/export`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(csrf ? { [CSRF_HEADER_NAME]: csrf } : {}),
    },
    body: JSON.stringify({ conversation_ids: conversationIds, format }),
  });

  if (!response.ok) {
    throw new Error(`Export failed with status ${response.status}`);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    saveViaAnchor(
      objectUrl,
      filenameFromHeader(
        response.headers.get('Content-Disposition'),
        'Unterhaltungen.zip'
      )
    );
  } finally {
    // Without this the blob stays in memory for the lifetime of the document.
    // A timeout, because revoking synchronously can beat the click in Safari.
    setTimeout(() => {
      URL.revokeObjectURL(objectUrl);
    }, 1000);
  }

  log.info('Bulk conversation export requested', {
    count: conversationIds.length,
    format,
  });
}
